/**
 * Multi-year Roth plan: the settings a strategy carries for converting over
 * several years instead of once. Nothing here calculates. The settings ride on
 * the strategy's input under `uiState`, which the engine ignores, so a saved
 * strategy with or without them is sent to the engine unchanged. The year loop
 * that runs them lives server-side and lands in a later step.
 */

export type RothPlanApproach = 'fillBracket' | 'fixed' | 'spread';

export interface RothPlanSettings {
  enabled: boolean;
  /** Fill a federal bracket each year, a fixed amount each year, or spread the IRA evenly to the end year. */
  approach: RothPlanApproach;
  /** The bracket to fill to when `approach` is 'fillBracket' (0.22 = 22%). */
  bracketRate: number;
  /** Per-year conversion when `approach` is 'fixed'. */
  fixedAmount: number;
  fromYear: number;
  toYear: number;
  /** 0 = ignore Medicare; 1-5 = keep income below that IRMAA tier. */
  medicareTierCap: number;
  /** The plan runs while someone is alive; these are the ages the engine stops at. */
  tpPlanToAge: number;
  spPlanToAge: number;
  tpRetireAge: number;
  spRetireAge: number;
  tpSsClaimAge: number;
  spSsClaimAge: number;
  /** Living costs each year, today's dollars; paid from brokerage, then Roth, then the IRA. */
  spendingPerYear: number;
  traditionalIraTp: number;
  traditionalIraSp: number;
  rothBalance: number;
  brokerageBalance: number;
  brokerageBasis: number;
  /** Give charity from the IRA after 70½ (QCD) instead of cash. */
  qcd: boolean;
  /** Pay each year's tax from brokerage rather than from the conversion. */
  payTaxFromBrokerage: boolean;
}

export const ROTH_PLAN_BRACKETS = [0.12, 0.22, 0.24, 0.32, 0.35] as const;
export const ROTH_PLAN_MEDICARE_TIERS = [1, 2, 3, 4, 5] as const;

export function defaultRothPlan(taxYear: number): RothPlanSettings {
  return {
    enabled: true,
    approach: 'fillBracket',
    bracketRate: 0.24,
    fixedAmount: 100000,
    fromYear: taxYear,
    toYear: taxYear + 9,
    medicareTierCap: 0,
    tpPlanToAge: 92,
    spPlanToAge: 94,
    tpRetireAge: 67,
    spRetireAge: 67,
    tpSsClaimAge: 70,
    spSsClaimAge: 67,
    spendingPerYear: 0,
    traditionalIraTp: 0,
    traditionalIraSp: 0,
    rothBalance: 0,
    brokerageBalance: 0,
    brokerageBasis: 0,
    qcd: true,
    payTaxFromBrokerage: true,
  };
}
